import { Link } from "react-router-dom";
import FomoFiLogo from "@/components/brand/FomoFiLogo";
import SectionLink from "@/components/layout/SectionLink";

/**
 * The one public footer. Used by MarketingLayout (landing, /features, /contact)
 * AND by LegalLayout (/privacy, /terms).
 *
 * WHY ONE FOOTER. /privacy and /terms used to render a second footer,
 * `SiteFooter`, whose only links were Privacy, Terms and a mailto. So clicking
 * Privacy from the landing dropped the Product and Company groups and left
 * "Back to home" as the only route onward — a dead end reachable from every
 * marketing page. Two footers also meant two link sets to keep in step, and the
 * legal one was already a subset that nobody had updated when /features and
 * /contact shipped. `SiteFooter` is deleted; LegalLayout was its only consumer.
 *
 * WHY THE TOKENS ARE SEMANTIC AND MUST STAY THAT WAY. This footer previously
 * hardcoded `bg-white` and eight `neutral-*` values, which was fine while it
 * only ever rendered on the marketing pages — those carry no `dark:` variants
 * and have no theme toggle. LegalLayout DOES: it is the only public surface with
 * a ThemeToggle, and it sets `dark:prose-invert` on the document body. Adopting
 * the footer here with raw colours would have put a hard-white panel with
 * neutral-600 text under a document the reader had switched to dark — trading a
 * navigation gap for a rendering defect. So the conversion came first, and it is
 * load-bearing rather than tidying:
 *
 *   bg-white            -> bg-background        (light: oklch(1 0 0) — identical)
 *   border-neutral-200  -> border-border        (light: oklch(0.922 0 0) — identical)
 *   text-neutral-900    -> text-foreground
 *   text-neutral-600/500-> text-muted-foreground
 *
 * The two identities are why the marketing pages are unchanged in light mode:
 * `--background` is pure white and `--border` is exactly neutral-200 at :root.
 * Do not reintroduce a raw colour here — CLAUDE.md forbids it, and this footer
 * now renders on a themed surface where it would be visible rather than moot.
 */
export default function MarketingFooter() {
  // `href` for on-page anchors / mailto; `to` for in-app routes (rendered as a
  // router Link). Dead links with no destination have been removed entirely so
  // nothing in the footer presents as clickable without going somewhere.
  const cols: {
    heading: string;
    links: { label: string; href?: string; to?: string }[];
  }[] = [
    {
      heading: "Product",
      // Features is the /features ROUTE, matching the header. It pointed at the
      // landing's #features band, so the same word in the same chrome went to
      // two different places depending on which one you clicked — Anthony,
      // twice: "features link is going to old features page". That band is gone
      // (item 9); this is the only Features destination now.
      links: [
        { label: "Features", to: "/features" },
        { label: "Pricing", href: "#pricing" },
        { label: "Security", href: "#security" },
      ],
    },
    {
      heading: "Company",
      // Was `mailto:support@fomofi.app` as a plain <a>, so on a machine with no
      // mail client configured, clicking it did nothing at all — which is very
      // likely what "Need a contact page" was reporting. /help can't serve this:
      // it sits inside OnboardingGate and no marketing visitor can reach it.
      links: [{ label: "Contact", to: "/contact" }],
    },
    {
      heading: "Legal",
      links: [
        { label: "Privacy", to: "/privacy" },
        { label: "Terms", to: "/terms" },
      ],
    },
  ];

  const linkClass = "text-[15px] text-muted-foreground transition hover:text-foreground";

  return (
    <footer className="border-t border-border bg-background">
      <div className="mx-auto grid max-w-[1440px] gap-10 px-10 py-16 lg:grid-cols-[1.4fr_1fr_1fr_1fr] lg:px-16">
        <div>
          <FomoFiLogo size="lg" />
          <p className="mt-4 max-w-xs text-[14px] leading-relaxed text-muted-foreground">
            Forecast your spending, own every paycheck, and stop the end-of-month surprise.
          </p>
          {/* AN-16 (Anthony, slide 5): no envelope icon here. Support stays reachable
              through Contact and the support address on the help pages. */}
        </div>

        {cols.map((col) => (
          <div key={col.heading}>
            <h4 className="text-[13px] font-bold uppercase tracking-[0.15em] text-foreground">
              {col.heading}
            </h4>
            <ul className="mt-4 space-y-2.5">
              {col.links.map((l) => (
                <li key={l.label}>
                  {l.to ? (
                    // AN-15 (Anthony, slide 5): the page mounts at the top, but a
                    // link to the page you're on doesn't remount it, so Features on
                    // /features did nothing. Same as the header logo: go to the top.
                    <Link to={l.to} onClick={() => window.scrollTo(0, 0)} className={linkClass}>
                      {l.label}
                    </Link>
                  ) : l.href?.startsWith("#") ? (
                    // Same cross-page resolution as the header nav — these were
                    // the other dead `#section` links once a second page existed.
                    <SectionLink hash={l.href} className={linkClass}>
                      {l.label}
                    </SectionLink>
                  ) : (
                    <a href={l.href} className={linkClass}>
                      {l.label}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="border-t border-border/60">
        <div className="mx-auto flex max-w-[1440px] flex-col items-center justify-between gap-3 px-10 py-6 text-[13px] text-muted-foreground sm:flex-row lg:px-16">
          <p>&copy; {new Date().getFullYear()} Fomo-Fi. All rights reserved.</p>
          <p>Made with clarity for your money.</p>
        </div>
      </div>
    </footer>
  );
}
